import React, { useState } from "react";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { EyeIcon, XMarkIcon, PaperClipIcon } from "@heroicons/react/20/solid";
import { db, storage } from "../../firebase";
import DocumentUploader from "./DocumentUploader";
import ArchivoPreviewModal from "./ArchivoPreviewModal";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import DialogoConfirmacion from "./DialogoConfirmacion";

const ACCENT = "#1b4d2e";
const EMERALD = "#059669";
const CALENDAR_ID = "default";

function fmtDate(value) {
  if (!value) return "";
  const d = value?.toDate?.() || (value instanceof Date ? value : new Date(value));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

function truncate(str, n) {
  if (!str) return "";
  if (str.length <= n) return str;
  return str.slice(0, n - 1) + "…";
}

function FilledChip({ archivo, label, color, onPreview, onRemove, soloLectura }) {
  return (
    <div
      className="group inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium"
      style={{
        borderColor: color + "55",
        backgroundColor: color + "12",
        color,
      }}
    >
      {/* Solo lectura (P-FRA-13): el chip dice que hay comprobante, pero no
          se abre ni se previsualiza. */}
      {soloLectura ? (
        <span className="inline-flex items-center gap-1">
          <PaperClipIcon className="h-3.5 w-3.5" />
          <span>{label}: {truncate(archivo?.filename || "comprobante", 28)}</span>
        </span>
      ) : (
      <button
        type="button"
        onClick={onPreview}
        className="inline-flex items-center gap-1 hover:underline"
        title={`Ver ${archivo?.filename || label}`}
      >
        <EyeIcon className="h-3.5 w-3.5" />
        <span>{label}: {truncate(archivo?.filename || "comprobante", 28)}</span>
      </button>
      )}
      {!soloLectura && (
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-zinc-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Quitar comprobante"
        aria-label="Quitar comprobante"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
      )}
    </div>
  );
}

function EmptyChip({ label, storagePathPrefix, onUploaded, disabled }) {
  return (
    <DocumentUploader
      storagePathPrefix={storagePathPrefix}
      buttonLabel={`+ ${label}`}
      withDownloadUrl={false}
      disabled={disabled}
      onUploaded={onUploaded}
    />
  );
}

function ComprobanteSlot({
  archivo,
  label,
  color,
  storagePathPrefix,
  onPreview,
  onUploaded,
  onRemove,
  disabled,
  soloLectura = false,
}) {
  if (archivo) {
    return (
      <FilledChip
        archivo={archivo}
        label={label}
        color={color}
        onPreview={onPreview}
        onRemove={onRemove}
        soloLectura={soloLectura}
      />
    );
  }
  // Solo lectura (inversionista, P-FRA-13): sin archivo no hay nada que ver
  // y adjuntar es escritura.
  if (soloLectura) return null;
  return (
    <EmptyChip
      label={label}
      storagePathPrefix={storagePathPrefix}
      onUploaded={onUploaded}
      disabled={disabled}
    />
  );
}

/**
 * ComprobanteFooter — variant for client depositos (archivos in subcollection).
 * Renders 1-2 chips: "Comprobante del pago" + (when devuelto) "Comprobante de devolución".
 */
export default function ComprobanteFooter({
  venueId,
  eventId,
  eventNombre,
  depositoId,
  fechaPago,
  fechaDevolucion,
  estado,
  pagoArchivo,
  devolucionArchivo,
  currentUser,
  userInfo,
  onChanged,
  soloLectura = false,
}) {
  const [previewArchivo, setPreviewArchivo] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // archivo a quitar

  const storagePrefix = venueId && eventId
    ? `venues/${venueId}/eventos/${eventId}/archivos`
    : null;

  const writeArchivo = async (role, payload) => {
    const archivoRef = doc(
      collection(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos")
    );
    const label = role === "devolucion"
      ? `Comprobante de devolución ${fmtDate(fechaDevolucion || fechaPago)}`.trim()
      : `Comprobante de depósito ${fmtDate(fechaPago)}`.trim();
    try {
      await setDoc(archivoRef, {
        label,
        tipo: "comprobante",
        storagePath: payload.storagePath,
        filename: payload.filename,
        fileSize: payload.fileSize,
        mimeType: payload.mimeType,
        uploadedAt: Timestamp.now(),
        uploadedBy: currentUser?.uid || null,
        uploadedByName: userInfo?.name || "",
        linkedTo: { kind: "deposito", id: depositoId },
        linkedRole: role,
      });
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.DEPOSITO_COMPROBANTE_ATTACH,
        entity: "deposito",
        entityId: depositoId,
        entityName: eventNombre || "",
        details: { role, filename: payload.filename, fileSize: payload.fileSize },
      }, userInfo, currentUser);
      onChanged?.();
    } catch (err) {
      console.error("Error saving comprobante doc:", err);
      showAlert("Se subió el archivo pero falló al guardar el comprobante.", "error");
      try { await deleteObject(ref(storage, payload.storagePath)); } catch (_) {}
    }
  };

  const removeArchivo = (archivo) => {
    if (!archivo) return;
    setConfirmRemove(archivo);
  };
  const doRemoveArchivo = async () => {
    const archivo = confirmRemove;
    setConfirmRemove(null);
    if (!archivo) return;
    try {
      if (archivo.storagePath) {
        try { await deleteObject(ref(storage, archivo.storagePath)); }
        catch (err) {
          if (err?.code !== "storage/object-not-found") {
            console.error("Error deleting blob:", err);
          }
        }
      }
      await deleteDoc(doc(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos", archivo.id));
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.DEPOSITO_COMPROBANTE_DELETE,
        entity: "deposito",
        entityId: depositoId,
        entityName: eventNombre || "",
        details: { role: archivo.linkedRole, filename: archivo.filename },
      }, userInfo, currentUser);
      onChanged?.();
    } catch (err) {
      console.error("Error removing comprobante:", err);
      showAlert("No se pudo quitar el comprobante.", "error");
    }
  };

  return (
    <>
      <ArchivoPreviewModal
        open={!!previewArchivo}
        onClose={() => setPreviewArchivo(null)}
        storagePath={previewArchivo?.storagePath}
        filename={previewArchivo?.filename}
        mimeType={previewArchivo?.mimeType}
      />
      <DialogoConfirmacion
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        titulo="Quitar comprobante"
        subtitulo={confirmRemove?.filename || ""}
        icono={PaperClipIcon}
        confirmar={{ label: "Quitar", tono: "rojo", onClick: doRemoveArchivo }}
      >
        <p>
          El archivo se elimina y deja de estar disponible para descargar o
          ver. Esta acción no se puede deshacer.
        </p>
      </DialogoConfirmacion>
      {!(soloLectura && !pagoArchivo && !(estado === "devuelto" && devolucionArchivo)) && (
      <div className="mt-3 pt-3 border-t border-zinc-200/80 flex flex-wrap gap-2">
        <ComprobanteSlot
          archivo={pagoArchivo}
          label="Comprobante del pago"
          color={ACCENT}
          storagePathPrefix={storagePrefix}
          onPreview={() => setPreviewArchivo(pagoArchivo)}
          onUploaded={(p) => writeArchivo("pago", p)}
          onRemove={() => removeArchivo(pagoArchivo)}
          disabled={!storagePrefix}
          soloLectura={soloLectura}
        />
        {estado === "devuelto" && (
          <ComprobanteSlot
            archivo={devolucionArchivo}
            label="Comprobante de devolución"
            color={EMERALD}
            storagePathPrefix={storagePrefix}
            onPreview={() => setPreviewArchivo(devolucionArchivo)}
            onUploaded={(p) => writeArchivo("devolucion", p)}
            onRemove={() => removeArchivo(devolucionArchivo)}
            disabled={!storagePrefix}
            soloLectura={soloLectura}
          />
        )}
      </div>
      )}
    </>
  );
}

/**
 * ComprobanteFooterEmbedded — variant for entities that embed the comprobante
 * as a field on the doc (proveedor depositos). Parent passes onAttachPago,
 * onAttachDevolucion, onRemovePago, onRemoveDevolucion, plus the existing
 * pago/devolucion objects.
 */
export function ComprobanteFooterEmbedded({
  storagePathPrefix,
  estado,
  comprobantePago,
  comprobanteDevolucion,
  onAttachPago,
  onAttachDevolucion,
  onRemovePago,
  onRemoveDevolucion,
  disabled,
  soloLectura = false,
}) {
  const [previewArchivo, setPreviewArchivo] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // { which, comp }

  const askRemove = (which, comp) => {
    if (!comp) return;
    setConfirmRemove({ which, comp });
  };
  const doRemove = async () => {
    const pending = confirmRemove;
    setConfirmRemove(null);
    if (!pending) return;
    const { which, comp } = pending;
    if (comp.storagePath) {
      try { await deleteObject(ref(storage, comp.storagePath)); }
      catch (err) {
        if (err?.code !== "storage/object-not-found") {
          console.error("Error deleting blob:", err);
        }
      }
    }
    which === "pago" ? onRemovePago?.() : onRemoveDevolucion?.();
  };

  return (
    <>
      <ArchivoPreviewModal
        open={!!previewArchivo}
        onClose={() => setPreviewArchivo(null)}
        storagePath={previewArchivo?.storagePath}
        filename={previewArchivo?.filename}
        mimeType={previewArchivo?.mimeType}
      />
      <DialogoConfirmacion
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        titulo="Quitar comprobante"
        subtitulo={confirmRemove?.comp?.filename || ""}
        icono={PaperClipIcon}
        confirmar={{ label: "Quitar", tono: "rojo", onClick: doRemove }}
      >
        <p>
          El archivo se elimina y deja de estar disponible para descargar o
          ver. Esta acción no se puede deshacer.
        </p>
      </DialogoConfirmacion>
      {!(soloLectura && !comprobantePago && !(estado === "devuelto" && comprobanteDevolucion)) && (
      <div className="mt-3 pt-3 border-t border-zinc-200/80 flex flex-wrap gap-2">
        <ComprobanteSlot
          archivo={comprobantePago}
          label="Comprobante del pago"
          color={ACCENT}
          storagePathPrefix={storagePathPrefix}
          onPreview={() => setPreviewArchivo(comprobantePago)}
          onUploaded={(p) => onAttachPago?.(p)}
          onRemove={() => askRemove("pago", comprobantePago)}
          disabled={disabled || !storagePathPrefix}
          soloLectura={soloLectura}
        />
        {estado === "devuelto" && (
          <ComprobanteSlot
            archivo={comprobanteDevolucion}
            label="Comprobante de devolución"
            color={EMERALD}
            storagePathPrefix={storagePathPrefix}
            onPreview={() => setPreviewArchivo(comprobanteDevolucion)}
            onUploaded={(p) => onAttachDevolucion?.(p)}
            onRemove={() => askRemove("devolucion", comprobanteDevolucion)}
            disabled={disabled || !storagePathPrefix}
            soloLectura={soloLectura}
          />
        )}
      </div>
      )}
    </>
  );
}

// Re-export the PaperClipIcon to keep this module's interface compact for
// callers that don't need it; the helper is exported above as default + named.
export { PaperClipIcon as _PaperClipIcon };
